/**
 * PreviewTab — admin plan-preview switcher (view app as free/premium/etc).
 * Extracted verbatim from AdminScreen.jsx (god-file decomposition).
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth, ADMIN_PREVIEW_PLANS } from '../context/AuthContext';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

export default function PreviewTab() {
  const { adminPreviewPlan, setAdminPreviewPlan, clearAdminPreviewPlan } = useAuth();

  const PLAN_ORDER = ['FREE', 'BASIC', 'PRO', 'ULTIMATE'];
  const PLAN_COLORS = {
    FREE: { accent: '#6B7280', label: 'Free', desc: 'No subscription — 10 queries/mo, 1 sportsbook, no SGP' },
    BASIC: { accent: '#3B82F6', label: 'Basic', desc: '100 queries/mo, 2 sportsbooks, 2-leg SGP suggestions' },
    PRO: { accent: '#A855F7', label: 'Pro', desc: '250 queries/mo, 4 sportsbooks, 3-leg SGP, CSV export' },
    ULTIMATE: { accent: '#F59E0B', label: 'Ultimate', desc: 'Unlimited queries, 8+ sportsbooks, full market set, custom alerts' },
  };

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 24, maxWidth: 700 }}>
      <Text style={pt.title}>User Experience Preview</Text>
      <Text style={pt.subtitle}>
        Simulate any subscription tier to QA feature gates and demo to clients.
        Your admin access is never affected — only the subscription view is overridden.
        The preview persists through page refreshes and clears when you close the tab.
      </Text>

      {adminPreviewPlan && (
        <View style={pt.activeBanner}>
          <Ionicons name="eye-outline" size={16} color="#10B981" />
          <Text style={pt.activeBannerText}>
            Currently previewing as <Text style={{ fontWeight: '700' }}>{adminPreviewPlan}</Text>
          </Text>
          <TouchableOpacity style={pt.clearBtn} onPress={clearAdminPreviewPlan}>
            <Text style={pt.clearBtnText}>Clear preview</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={pt.planGrid}>
        {PLAN_ORDER.map(key => {
          const p = PLAN_COLORS[key];
          const cfg = ADMIN_PREVIEW_PLANS[key];
          const active = adminPreviewPlan === key;
          return (
            <TouchableOpacity
              key={key}
              style={[pt.planCard, { borderColor: active ? p.accent : p.accent + '30', backgroundColor: active ? p.accent + '15' : 'rgba(255,255,255,0.02)' }]}
              onPress={() => active ? clearAdminPreviewPlan() : setAdminPreviewPlan(key)}
              activeOpacity={0.8}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <View style={[pt.badge, { backgroundColor: p.accent + '22', borderColor: p.accent + '55' }]}>
                  <Text style={[pt.badgeText, { color: p.accent }]}>{p.label}</Text>
                </View>
                {active && (
                  <View style={pt.activePill}>
                    <Ionicons name="eye" size={11} color="#10B981" />
                    <Text style={pt.activePillText}>Active</Text>
                  </View>
                )}
              </View>
              <Text style={pt.planDesc}>{p.desc}</Text>
              <View style={pt.limitsRow}>
                <LimitChip label="Queries" value={cfg.queryLimit === -1 ? '∞' : String(cfg.queryLimit)} color={p.accent} />
                <LimitChip label="Books" value={cfg.maxSportsbooks >= 99 ? '∞' : String(cfg.maxSportsbooks)} color={p.accent} />
                <LimitChip label="SGP legs" value={String(cfg.maxSGPLegs)} color={p.accent} />
              </View>
              <Text style={[pt.tapHint, active && { color: '#EF4444' }]}>
                {active ? 'Tap to exit preview' : 'Tap to preview as this plan'}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <View style={pt.infoBox}>
        <Ionicons name="information-circle-outline" size={16} color="rgba(255,255,255,0.4)" style={{ marginTop: 1 }} />
        <Text style={pt.infoText}>
          The floating banner at the top of every page lets you switch plans instantly during demos.
          Navigate to Profile, Plans, or any feature-gated screen to show the client exactly what each tier looks like.
        </Text>
      </View>
    </ScrollView>
  );
}

function LimitChip({ label, value, color }) {
  return (
    <View style={[pt.limitChip, { borderColor: color + '30' }]}>
      <Text style={[pt.limitValue, { color }]}>{value}</Text>
      <Text style={pt.limitLabel}>{label}</Text>
    </View>
  );
}

const pt = StyleSheet.create({
  title: { ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal'), color: uiVar('adm-ink', '#fff'), fontSize: 22, fontWeight: '700', marginBottom: 6 },
  subtitle: { color: 'rgba(255,255,255,0.45)', fontSize: 13, lineHeight: 20, marginBottom: 24 },
  activeBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    backgroundColor: 'rgba(16,185,129,0.1)', borderWidth: 1, borderColor: 'rgba(16,185,129,0.25)',
    borderRadius: 10, padding: 12, marginBottom: 20,
  },
  activeBannerText: { color: '#10B981', fontSize: 13, flex: 1 },
  clearBtn: {
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 7,
    backgroundColor: 'rgba(239,68,68,0.12)', borderWidth: 1, borderColor: 'rgba(239,68,68,0.25)',
  },
  clearBtnText: { color: '#EF4444', fontSize: 12, fontWeight: '700' },
  planGrid: { gap: 12 },
  planCard: { borderWidth: 1, borderRadius: 14, padding: 18 },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 7, borderWidth: 1 },
  badgeText: { fontSize: 13, fontWeight: '800', letterSpacing: 0.3 },
  activePill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
  },
  activePillText: { color: '#10B981', fontSize: 11, fontWeight: '700' },
  planDesc: { color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 19, marginBottom: 14 },
  limitsRow: { flexDirection: 'row', gap: 10, marginBottom: 14 },
  limitChip: {
    alignItems: 'center', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8,
    borderWidth: 1, backgroundColor: 'rgba(255,255,255,0.03)',
  },
  limitValue: { fontSize: 16, fontWeight: '800' },
  limitLabel: { color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: '600', marginTop: 1 },
  tapHint: { color: 'rgba(255,255,255,0.25)', fontSize: 11, fontStyle: 'italic' },
  infoBox: {
    flexDirection: 'row', gap: 10, alignItems: 'flex-start',
    backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 14, marginTop: 24,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.07)',
  },
  infoText: { color: 'rgba(255,255,255,0.4)', fontSize: 12, lineHeight: 18, flex: 1 },
});

// ─────────────────────────────────────────────────────────────────────────────

